import type { ReactNode } from 'react';

/** Props for {@link EmptyState}. */
export interface EmptyStateProps {
  /** What this screen/section is (spec 08 §6: explains the emptiness). */
  title: string;
  /** Why it is empty and/or what the user can do next. */
  description?: ReactNode;
  /** Optional illustrative icon rendered above the title. */
  icon?: ReactNode;
  /** Optional primary next action (e.g. a Button). */
  action?: ReactNode;
  /** Optional extra classes. */
  className?: string;
}

/**
 * Friendly "nothing here yet" placeholder.
 *
 * Per spec 08 §6 an empty state must explain what the screen is, why it is empty,
 * and offer the primary next action. Centred, readable in both themes, and safe
 * down to 360px.
 */
export function EmptyState({ title, description, icon, action, className = '' }: EmptyStateProps): JSX.Element {
  return (
    <div
      className={`flex flex-col items-center justify-center px-6 py-12 text-center ${className}`.trim()}
    >
      {icon ? (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-navy-bg dark:text-slate-400">
          {icon}
        </div>
      ) : null}
      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      {description ? (
        <p className="mt-1.5 max-w-md text-sm text-slate-500 dark:text-slate-400">{description}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}
